import { useContext } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { redTheme } from 'src/styles/theme';
import { EventPageContext } from 'src/modules/events/components/EventPage/EventPageContext';
import { StyleProps } from '@summer/jst-react';
import { useFlattenEvent } from 'src/modules/events/hooks/useFlattenEvent';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';

const Section = styled.section<{ $background?: string }>`
  padding: 27px 0 31px;
  background: ${({ theme, $background }) =>
    $background ?? theme.palette.accent};
  color: ${({ theme }) => theme.text.palette.primary};
  ${media.w.greaterThanOrEqual.desktop} {
    padding: 32px 0 37px;
  }
`;

const Container = styled(ConstraintContainer)`
  max-width: 900px;
  ${media.w.greaterThanOrEqual.desktop} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const HeadingContainer = styled.div`
  margin-bottom: 26px;
  ${media.w.greaterThanOrEqual.desktop} {
    max-width: 280px;
  }
`;

const Heading = styled.h3`
  font-weight: bold;
  font-size: 16px;
  line-height: 120%;
  margin-bottom: 15px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${media.w.greaterThanOrEqual.desktop} {
    font-weight: 600;
    font-size: 18px;
    margin-bottom: 22px;
  }
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const EventPageHowToParticipate = ({ ...props }: StyleProps) => {
  const { _ } = useTranslate();
  const { isUpcoming, event } = useContext(EventPageContext);

  const flatEvent = useFlattenEvent(event);

  if (!isUpcoming || !flatEvent.howToParticipate) {
    return null;
  }

  return (
    <ThemeProvider theme={redTheme}>
      <Section $background={flatEvent.background} {...props}>
        <Container>
          <HeadingContainer>
            <Heading>{_('How to participate')}</Heading>
            <Description>
              <Markdown>{flatEvent.howToParticipate}</Markdown>
            </Description>
          </HeadingContainer>
        </Container>
      </Section>
    </ThemeProvider>
  );
};
